import React, { useCallback, useMemo } from 'react'
import { usePanelPatientUid } from '../../dashboard/panelContext'
import { toIso } from '../../format'
import {
  CardFooter,
  FieldRow,
  FieldSection,
  LabeledCheckbox,
  LabeledDate,
  LabeledSelect,
  LabeledText,
} from '../fieldPrimitives'
import { GENDER_IDENTITY_OPTIONS, MARITAL_STATUS_OPTIONS } from './patientRecordOptions'
import { BASIC_DETAILS_SCHEMA } from './patientRecordSchema'
import { makeFieldSetter, useCardAutosave } from './useCardAutosave'
import { usePatientRecord } from './usePatientRecord'
import { useSources } from './useSources'
import { useUpdatePatientRecord } from './useUpdatePatientRecord'

type BasicDetails = {
  firstname: string
  lastname: string
  preferredName: string
  email: string
  phone: string
  genderIdentity: string
  maritalStatus: string
  source: string
  birthDate: string
  isDependent: boolean
}

const BasicDetailsPanel = () => {
  const uid = usePanelPatientUid()
  const { loading, patient, details } = usePatientRecord(uid)
  const sources = useSources()
  const update = useUpdatePatientRecord(uid)

  const initial = useMemo<BasicDetails>(
    () => ({
      firstname: patient?.firstname ?? '',
      lastname: patient?.lastname ?? '',
      preferredName: patient?.preferredName ?? '',
      email: patient?.email ?? '',
      phone: patient?.phone ?? '',
      genderIdentity: patient?.genderIdentity ?? '',
      maritalStatus: patient?.maritalStatus ?? '',
      source: details.source ?? '',
      birthDate: toIso(patient?.birthDate)?.slice(0, 10) ?? '',
      isDependent: details.isDependent ?? false,
    }),
    [
      patient?.firstname,
      patient?.lastname,
      patient?.preferredName,
      patient?.email,
      patient?.phone,
      patient?.genderIdentity,
      patient?.maritalStatus,
      details.source,
      patient?.birthDate,
      details.isDependent,
    ]
  )

  const save = useCallback(
    (next: BasicDetails) =>
      update(
        {
          firstname: next.firstname,
          lastname: next.lastname,
          preferredName: next.preferredName,
          email: next.email,
          phone: next.phone,
          genderIdentity: next.genderIdentity,
          maritalStatus: next.maritalStatus,
          birthDate: next.birthDate || null,
        },
        {
          // `source: null` is rejected server-side — omit it entirely rather than sending null.
          source: next.source || undefined,
          isDependent: next.isDependent,
        }
      ),
    [update]
  )

  const { values, setValues, error, status } = useCardAutosave(initial, BASIC_DETAILS_SCHEMA, save)
  const set = makeFieldSetter(setValues)

  return (
    <FieldSection>
      <FieldRow columns={3}>
        <LabeledText label="First name" value={values.firstname} onChange={set('firstname')} required />
        <LabeledText label="Last name" value={values.lastname} onChange={set('lastname')} required />
        <LabeledText label="Preferred name" value={values.preferredName} onChange={set('preferredName')} />
      </FieldRow>
      <FieldRow columns={2}>
        <LabeledText label="Email" value={values.email} onChange={set('email')} required />
        <LabeledText label="Phone" value={values.phone} onChange={set('phone')} />
      </FieldRow>
      <FieldRow columns={2}>
        <LabeledSelect
          label="Gender Identity"
          value={values.genderIdentity}
          onChange={set('genderIdentity')}
          options={GENDER_IDENTITY_OPTIONS}
        />
        <LabeledSelect
          label="Marital Status"
          value={values.maritalStatus}
          onChange={set('maritalStatus')}
          options={MARITAL_STATUS_OPTIONS}
        />
      </FieldRow>
      <FieldRow columns={2}>
        <LabeledSelect
          label="Source"
          value={values.source}
          onChange={set('source')}
          options={sources.map((source) => ({ value: source, label: source }))}
        />
        <LabeledDate label="Birthdate" value={values.birthDate} onChange={set('birthDate')} />
      </FieldRow>
      <LabeledCheckbox label="Is a dependent or spouse?" checked={values.isDependent} onChange={set('isDependent')} />
      <CardFooter loading={loading} status={status} error={error} />
    </FieldSection>
  )
}

export default BasicDetailsPanel
